import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

export type AttendantRequestKind =
  | 'weight_mismatch'
  | 'age_restricted'
  | 'unknown_item'
  | 'skip_bagging'
  | 'manual_price'
  | 'random_recheck'
  | 'generic_call';

export type AttendantRequestPriority = 'P1' | 'P2' | 'P3' | 'P4' | 'P5';

export type AttendantRequestState =
  | 'pending'
  | 'claimed'
  | 'approved'
  | 'declined'
  | 'escalated'
  | 'expired';

export type AttendantDecisionOutcome = 'approve' | 'decline' | 'escalate';

export type AttendantRequestSummary = {
  id: string;
  clientUuid: string;
  terminalId: string;
  kind: AttendantRequestKind;
  priority: AttendantRequestPriority;
  state: AttendantRequestState;
  payload: Record<string, unknown>;
  actorCashierId: string | null;
  itemId: string | null;
  claimedByUserId: string | null;
  claimedAt: string | null;
  decisionOutcome: AttendantDecisionOutcome | null;
  decisionByUserId: string | null;
  decisionAt: string | null;
  decisionNotes: string | null;
  requestedAt: string;
  expiresAt: string | null;
  lockVersion: number;
  createdAt: string;
  updatedAt: string;
};

export type AttendantRequestDetail = AttendantRequestSummary & {
  terminalLabel: string | null;
  cashierDisplayName: string | null;
  itemName: string | null;
  claimedByDisplayName: string | null;
  decisionByDisplayName: string | null;
};

export type ApprovalsListResponse = {
  items: AttendantRequestSummary[];
  total: number;
  page: number;
  pageSize: number;
};

export type ApprovalsListQuery = {
  state?: AttendantRequestState[];
  priority?: AttendantRequestPriority[];
  kind?: AttendantRequestKind[];
  terminalId?: string;
  page?: number;
  pageSize?: number;
};

const LIST_KEY = ['approvals'] as const;
const ONE_KEY = (id: string) => ['approvals', id] as const;

export function useApprovals(query: ApprovalsListQuery = {}) {
  return useQuery({
    queryKey: [...LIST_KEY, query] as const,
    queryFn: async (): Promise<ApprovalsListResponse> => {
      const params: Record<string, string | number> = {};
      if (query.page !== undefined) params.page = query.page;
      if (query.pageSize !== undefined) params.pageSize = query.pageSize;
      if (query.state !== undefined && query.state.length > 0) {
        params['filter[state]'] = query.state.join(',');
      }
      if (query.priority !== undefined && query.priority.length > 0) {
        params['filter[priority]'] = query.priority.join(',');
      }
      if (query.kind !== undefined && query.kind.length > 0) {
        params['filter[kind]'] = query.kind.join(',');
      }
      if (query.terminalId !== undefined) params['filter[terminalId]'] = query.terminalId;
      const res = await apiClient.get<ApprovalsListResponse>('/approvals', { params });
      return res.data;
    },
    // ADR-0010: BOS is an audit/read model, not the live SCO↔attendant
    // transport. Mutations invalidate this cache; operators may also use
    // the page refresh path when reviewing newly uploaded records.
  });
}

export function useApproval(id: string | undefined) {
  return useQuery({
    queryKey: id === undefined ? ['approvals', '__noop'] : ONE_KEY(id),
    enabled: id !== undefined,
    queryFn: async (): Promise<AttendantRequestDetail> => {
      const res = await apiClient.get<AttendantRequestDetail>(`/approvals/${id}`);
      return res.data;
    },
  });
}

export function useClaimApproval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<AttendantRequestSummary> => {
      const res = await apiClient.post<AttendantRequestSummary>(`/approvals/${id}/claim`, {});
      return res.data;
    },
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: LIST_KEY });
      qc.invalidateQueries({ queryKey: ONE_KEY(id) });
    },
  });
}

export function useReleaseApproval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<AttendantRequestSummary> => {
      const res = await apiClient.post<AttendantRequestSummary>(`/approvals/${id}/release`, {});
      return res.data;
    },
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: LIST_KEY });
      qc.invalidateQueries({ queryKey: ONE_KEY(id) });
    },
  });
}

export function useDecideApproval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      id: string;
      outcome: AttendantDecisionOutcome;
      notes?: string;
    }): Promise<AttendantRequestSummary> => {
      const res = await apiClient.post<AttendantRequestSummary>(`/approvals/${vars.id}/decide`, {
        outcome: vars.outcome,
        notes: vars.notes,
      });
      return res.data;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: LIST_KEY });
      qc.invalidateQueries({ queryKey: ONE_KEY(vars.id) });
    },
  });
}

// ---------- Display helpers ----------

export const PRIORITY_LABEL: Record<AttendantRequestPriority, string> = {
  P1: 'P1 · Age',
  P2: 'P2 · Weight / Unknown / Price',
  P3: 'P3 · Skip-bag',
  P4: 'P4 · Re-check',
  P5: 'P5 · Call',
};

export const KIND_LABEL: Record<AttendantRequestKind, string> = {
  weight_mismatch: 'Weight mismatch',
  age_restricted: 'Age restricted',
  unknown_item: 'Unknown item',
  skip_bagging: 'Skip bagging',
  manual_price: 'Manual price',
  random_recheck: 'Random re-check',
  generic_call: 'Call attendant',
};

export const STATE_LABEL: Record<AttendantRequestState, string> = {
  pending: 'Pending',
  claimed: 'Claimed',
  approved: 'Approved',
  declined: 'Declined',
  escalated: 'Escalated',
  expired: 'Expired',
};

/**
 * Catalogue keys for the three label maps above.
 *
 * The maps stay as the baked English, so a caller resolves a label as
 * `t(PRIORITY_LABEL_KEY[p], PRIORITY_LABEL[p])`. Each key is written out rather
 * than composed from the enum value at the call site: the parity guard reads the
 * source for literal keys, and a key it cannot see is a key nobody notices is
 * missing.
 */
export const PRIORITY_LABEL_KEY: Record<AttendantRequestPriority, string> = {
  P1: 'approvals.priority.p1',
  P2: 'approvals.priority.p2',
  P3: 'approvals.priority.p3',
  P4: 'approvals.priority.p4',
  P5: 'approvals.priority.p5',
};

export const KIND_LABEL_KEY: Record<AttendantRequestKind, string> = {
  weight_mismatch: 'approvals.kind.weightMismatch',
  age_restricted: 'approvals.kind.ageRestricted',
  unknown_item: 'approvals.kind.unknownItem',
  skip_bagging: 'approvals.kind.skipBagging',
  manual_price: 'approvals.kind.manualPrice',
  random_recheck: 'approvals.kind.randomRecheck',
  generic_call: 'approvals.kind.genericCall',
};

export const STATE_LABEL_KEY: Record<AttendantRequestState, string> = {
  pending: 'approvals.state.pending',
  claimed: 'approvals.state.claimed',
  approved: 'approvals.state.approved',
  declined: 'approvals.state.declined',
  escalated: 'approvals.state.escalated',
  expired: 'approvals.state.expired',
};

/**
 * Format the age of a request (since requestedAt) for display.
 * Returns "Xs", "Xm" or "Xh" — the column is dense and we don't
 * need calendar precision.
 */
export function ageSince(iso: string, now = Date.now()): string {
  const ms = now - new Date(iso).getTime();
  const sec = Math.max(0, Math.floor(ms / 1000));
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  return `${hr}h`;
}
